import dayjs from 'dayjs';

/**
 * Hook used to handle custom timeframe object.
 */
export function useTimeframe() {
  const unitStrings = {
    singular: {
      m: 'month',
      w: 'week',
      d: 'day',
    },
    plural: {
      m: 'months',
      w: 'weeks',
      d: 'days',
    },
  };

  const UNIT_MAP = { 'd': 'days', 'w': 'weeks', 'm': 'months' };
  const ERROR_MSG = `We didn't recognize the timeframe you entered. Please enter something like '12+ months',
    '5-6 months', '3 weeks', or '2 days after'.
    You can also enter special phrases like 'day before' or 'wedding day'.`;

  const toString = (timeframe) => {
    if (!timeframe){
      return '';
    }
    if (timeframe.is_day_of) {
      return 'Day of the Event';
    }
    if (timeframe.is_day_before) {
      return 'Day Before';
    }
    if (timeframe.is_day_after) {
      return 'Day After';
    }
    if (timeframe.is_everything_after) {
      return 'After the Event';
    }
    let str = '';
    if (timeframe.range) {
      str += timeframe.range.start;
      if (timeframe.range.end){
        str += `- ${timeframe.range.end}`;
      }else{
        str += '+';
      }
      str += ` ${unitStrings.plural[timeframe.units]}`;
    }else{
      str += timeframe.value;
      str += ` ${unitStrings[timeframe.value === 1 ? 'singular' : 'plural'][timeframe.units]}`;
    }
    if (timeframe.is_after) {
      str += ' after';
    }
    return str;
    // 8-9 months | 8-9m
    // 7 months | 7mo | 7 mos
    // 4 weeks | 4w | 4wk | 4wks
    // 2 days | 2d
    // Day before
    // Wedding Day | Day of
    // Day after
    // 1 week after
  };

  const fromString = (str) => {
    let newStr = str.trim().toLowerCase();
    if (newStr.length === 0) {
      return null;
    }

    if (newStr.match(/^(the )?(wedding day|day of|wedding date|wedding|big day|day of the event)$/)) {
      return { is_day_of: true };
    }
    if (newStr.match(/^(the )?day before$/)) {
      return { is_day_before: true };
    }
    if (newStr.match(/^(the )?day after$/)) {
      return { is_day_after: true };
    }
    let matches = str.match(/((before|ahead|ahead\s+of)(?:(?:\s*the)?\s*wedding)?)$/);
    if (matches) {
      newStr = newStr.substr(0, newStr.length - matches[1].length).trim();
    } else {
      matches = newStr.match(/(out)$/);
      if (matches) {
        newStr = newStr.substr(0, newStr.length - matches[1].length).trim();
      }
    }
    matches = newStr.match(/(after(?:(?:\s*the)?\s*event)?)$/);
    const timeframe = {};
    if (matches) {
      newStr = str.substr(0, newStr.length - matches[1].length).trim();
      if (newStr.length === 0){
        timeframe.is_everything_after = true;
        return timeframe;
      }
      timeframe.is_after = true;
    }
    matches = newStr.match(/(m|ms|mo|mos|mon|mons|month|months|w|ws|wk|wks|week|weeks|d|ds|day|days)$/);
    if (matches) {
      timeframe.units = matches[1].substr(0, 1);
      newStr = newStr.substr(0, newStr.length - matches[1].length).trim();
    }else{
      return undefined;
    }
    matches = newStr.match(/^(\d+)\s*(?:-|to\/)\s*(\d+)$/);
    if (matches) {
      timeframe.range = {
        start: parseInt(matches[1], 10),
        end: parseInt(matches[2], 10),
      };
      if (timeframe.range.start > timeframe.range.end) {
        const tmp = timeframe.range.start;
        timeframe.range.start = timeframe.range.end;
        timeframe.range.end = tmp;
      }
      if (timeframe.range.start === 0) {
        return undefined;
      }
      if (timeframe.range.start === timeframe.range.end) {
        return undefined;
      }
      return timeframe;
    }
    matches = newStr.match(/^(\d+)\s*(?:\+|plus)$/);
    if (matches) {
      timeframe.range = {
        start: parseInt(matches[1], 10),
      };
      if (timeframe.range.start === 0) {
        return undefined;
      }
      return timeframe;
    }
    matches = newStr.match(/^(\d+)$/);
    if (matches) {
      timeframe.value = parseInt(matches[1], 10);
      if (timeframe.value === 0) {
        if (timeframe.units === 'd') {
          return { is_day_of: true };
        }
        return undefined;
      }
      if (timeframe.value === 1 && timeframe.units === 'd') {
        if (timeframe.is_after) {
          return { is_day_after: true };
        }
        return { is_day_before: true };
      }
      return timeframe;
    }
    return undefined;
  };

  const dateForSortingRelativeToDate = (timeframe, date) => {
    let value;
    let units;
    if (timeframe.is_day_before) {
      value = -1;
      units = 'days';
    } else if (timeframe.is_day_of) {
      return dayjs(date);
    } else if (timeframe.is_day_after) {
      value = 1;
      units = 'days';
    } else if (timeframe.is_everything_after) {
      value = 10;
      units = 'years';
    } else {
      if (timeframe.range) {
        value = timeframe.range.start;
      } else {
        value = timeframe.value;
      }
      units = UNIT_MAP[timeframe.units];
      if (!timeframe.is_after){
        value = -value;
      }
    }
    return dayjs(date).add(value, units);
  };

  const timeframeIsEqual = (a, b) => {
    return toString(a) === toString(b);
  };

  const dateRangeRelativeToDate = (timeframe, date) => {
    let start = dateForSortingRelativeToDate(timeframe, date);
    if (timeframe.is_everything_after) {
      start = dayjs(date).add(1, 'days');
      return {
        start,
        end: null,
        units: 'd',
      };
    }
    let end = null;
    const range = {};
    if (timeframe.range) {
      if (timeframe.range.end) {
        let value = timeframe.range.end;
        const units = UNIT_MAP[timeframe.units];
        if (!timeframe.is_after) {
          value = -value;
        }
        end = dayjs(date).add(value, units);
      }
      if (timeframe.is_after) {
        range.start = start;
        range.end = end;
      } else {
        range.start = end;
        range.end = start;
      }
    } else {
      range.start = start;
      range.end = start;
    }
    range.units = timeframe.units;
    if (!range.units) {
      range.units = 'd';
    }
    return range;
  };

  return {
    UNIT_MAP,
    ERROR_MSG,
    toString,
    fromString,
    dateForSortingRelativeToDate,
    timeframeIsEqual,
    dateRangeRelativeToDate,
  };
};
